import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react"
import { cn } from "@strivelabs/ui-core"
import {
  clipLabel,
  dueDistance,
  dueLabel,
  dueState,
  momentWords,
  shortDate,
  shortenLabel,
  splitMatch,
  type DueState,
  swatchColor,
  swatchFor,
} from "./format"
import { ListTableHoverRow, ListTableHoverTarget } from "./hover-card"

/**
 * The parts a list table's cells are built from. Each is a plain component that
 * renders on its own, and inside a `ListTable` the ones that can say more carry
 * the table's hover card.
 */

/** A search hit inside text: the matched characters one step up in weight, nothing else. */
export const ListTableHighlight = ({ text, query }: { text: string; query?: string | null }) => {
  const parts = splitMatch(text, query)
  if (!parts) return <>{text}</>
  return (
    <>
      {parts[0]}
      <b className="font-semibold">{parts[1]}</b>
      {parts[2]}
    </>
  )
}

/** A label's colour dot, the 6px circle every chip and card draws. */
const Dot = ({ color, className }: { color: string; className?: string }) => (
  <i className={cn("size-1.5 shrink-0 rounded-full", className)} style={{ background: color }} />
)

/** The colour a label's name picks. */
const labelDot = (name: string) => swatchColor(swatchFor(name))

/** A label's React key: its id, or its name and place when it has none. */
const labelKey = (label: ListTableLabel, index: number) => label.id ?? `${label.name}-${index}`

/** A hover card's body that wraps: a whole title, a whole label name. */
const WrappedName = ({ children, dot, wide }: { children: ReactNode; dot?: string; wide?: boolean }) => (
  <div
    data-hc={wide ? "wide" : "fix"}
    className="flex items-start gap-[7px] px-[7px] py-[3px] text-[12px] leading-[18px] text-v2-list-ink"
  >
    {dot ? <Dot color={dot} className="mt-1.5" /> : null}
    <span className="min-w-0 whitespace-normal [overflow-wrap:anywhere]">{children}</span>
  </div>
)

export interface ListTableTitleProps {
  children: string
  /** Muted second line, for a row tall enough to hold it. */
  subtitle?: string
  /** Search query to mark inside the title. */
  query?: string | null
  className?: string
}

/**
 * The row's name. Cut with an ellipsis when the column runs out, and then shown
 * whole in a hover card hung from the column's left edge, so the card opens in
 * the same place on every row whatever the row's other cells hold.
 */
export const ListTableTitle = ({ children, subtitle, query, className }: ListTableTitleProps) => {
  const title = (
    <ListTableHoverTarget
      when="clipped"
      anchor="start"
      card={<WrappedName wide>{children}</WrappedName>}
      className={cn(
        "block min-w-0 truncate text-[14px] font-[450] leading-[21px] text-v2-list-ink",
        !subtitle && className,
      )}
    >
      <ListTableHighlight text={children} query={query} />
    </ListTableHoverTarget>
  )

  if (!subtitle) return title

  return (
    <span className={cn("flex min-w-0 flex-col", className)}>
      {title}
      <span className="truncate text-[12px] leading-[18px] text-v2-list-faint">{subtitle}</span>
    </span>
  )
}

/** A system state: its mark, then its word. The colour lives on the mark alone. */
export const ListTableStatus = ({ icon, label, className }: {
  icon: ReactNode
  label: ReactNode
  className?: string
}) => (
  <span className={cn("flex min-w-0 items-center gap-2 overflow-hidden", className)}>
    <span className="inline-flex size-4 shrink-0 items-center justify-center [&_svg]:size-4">{icon}</span>
    <span className="min-w-0 truncate text-[13px] leading-[1.4] text-v2-muted-foreground">{label}</span>
  </span>
)

/** Secondary text in a cell: a URL, a count, a short value. */
export const ListTableText = ({ children, tone = "muted", truncate = true, className }: {
  children: ReactNode
  tone?: "ink" | "muted" | "faint"
  /**
   * End in an ellipsis when it runs out of room. Off for a column that fades
   * its edge instead (`fade` on the column), where an ellipsis would sit under
   * the fade and say the same thing twice.
   */
  truncate?: boolean
  className?: string
}) => (
  <span
    className={cn(
      "min-w-0 text-[13px] leading-[1.4]",
      truncate ? "truncate" : "overflow-hidden whitespace-nowrap",
      tone === "ink" && "text-v2-list-ink",
      tone === "muted" && "text-v2-muted-foreground",
      tone === "faint" && "text-v2-list-faint",
      className,
    )}
  >
    {children}
  </span>
)

const CHIP = cn(
  "relative inline-flex h-5 shrink-0 items-center gap-[5px] overflow-hidden whitespace-nowrap rounded-full",
  "border border-v2-list-line bg-v2-card text-[12px] leading-none",
)

/** A small pill: a label, a date. */
export const ListTableChip = ({ children, dot, className }: {
  children: ReactNode
  /** A colour for the dot before the words. */
  dot?: string
  className?: string
}) => (
  <span className={cn(CHIP, "px-2 text-v2-list-faint", className)}>
    {dot ? <Dot color={dot} /> : null}
    {children}
  </span>
)

export interface ListTableLabel {
  id?: string
  name: string
}

/**
 * Whether the page's web fonts have finished loading. A chip measured in the
 * fallback face is narrower than the same chip in Inter, so a fit taken before
 * the swap can leave the `+N` half under the column's fade. Starts true when
 * the fonts were already there, so a list mounted later does not fit twice.
 */
const fontsSettled = () => typeof document === "undefined" || !document.fonts || document.fonts.status === "loaded"

const useFontsLoaded = () => {
  const [ loaded, setLoaded ] = useState(fontsSettled)
  useEffect(() => {
    if (loaded || typeof document === "undefined" || !document.fonts) return undefined
    let live = true
    document.fonts.ready.then(() => {
      if (live) setLoaded(true)
    })
    return () => {
      live = false
    }
  }, [ loaded ])
  return loaded
}

/** The most labels named before the rest fold into a `+N`. */
const MAX_NAMED = 2

const MoreChip = ({ rest }: { rest: ListTableLabel[] }) => (
  <ListTableHoverTarget
    card={(
      <div data-hc="fix" className="flex flex-wrap gap-1 px-1 py-[3px]">
        {rest.map((label, index) => (
          <ListTableChip key={labelKey(label, index)} dot={labelDot(label.name)}>
            {clipLabel(label.name)}
          </ListTableChip>
        ))}
      </div>
    )}
    className={cn(CHIP, "px-[7px] text-v2-list-faint")}
  >
    <span className="inline-flex shrink-0 items-center">
      {rest.slice(0, 3).map((label, index) => (
        <Dot
          key={labelKey(label, index)}
          color={labelDot(label.name)}
          className={cn("relative shadow-[0_0_0_1.5px_hsl(var(--v2-card))]", index > 0 && "-ml-0.5")}
        />
      ))}
    </span>
    +{rest.length} label{rest.length === 1 ? "" : "s"}
  </ListTableHoverTarget>
)

/**
 * A row's labels, chosen to fit their column rather than squeezed into it.
 *
 * Up to two are named and the rest fold into a `+N` that carries their colours.
 * When that does not fit, names come off the end into the `+N` — which is never
 * the one to go, because a column with more to say has to say so — down to the
 * `+N` alone. Only if even that cannot fit does one name come back and give up
 * characters.
 * Measured after rendering, because only a rendered chip knows how wide it is.
 */
export const ListTableLabels = ({ labels, query, className }: {
  labels: ListTableLabel[]
  query?: string | null
  className?: string
}) => {
  const ref = useRef<HTMLSpanElement>(null)
  const fonts = useFontsLoaded()
  // A different set of labels, or the web font arriving and widening every
  // chip, starts the fit again from the most it could show. Derived during
  // render rather than reset in an effect, so a measurement can never be taken
  // against one set of labels and applied to the next.
  const fresh = {
    key: `${labels.map((label) => label.name).join("\u0000")}|${fonts}`,
    named: Math.min(MAX_NAMED, labels.length),
    chars: 0,
  }
  const [ held, setFit ] = useState(fresh)
  const fit = held.key === fresh.key ? held : fresh

  useLayoutEffect(() => {
    const box = ref.current
    if (!box || box.scrollWidth <= box.clientWidth + 0.5) return
    // Names come off the end into the `+N`, down to the `+N` alone.
    if (fit.chars === 0 && fit.named > 0) {
      setFit({ key: fit.key, named: fit.named - 1, chars: 0 })
      return
    }
    // Even the `+N` alone does not fit: one name comes back and gives up characters.
    if (fit.chars === 0) {
      setFit({ key: fit.key, named: 1, chars: clipLabel(labels[0].name).length - 1 })
      return
    }
    if (fit.chars > 1) setFit({ key: fit.key, named: 1, chars: fit.chars - 1 })
  }, [ fit.key, fit.named, fit.chars, labels ])

  if (labels.length === 0) return null

  const named = labels.slice(0, fit.named)
  const rest = labels.slice(fit.named)

  return (
    <span ref={ref} className={cn("flex w-full min-w-0 items-center gap-[5px] overflow-hidden", className)}>
      {named.map((label, index) => {
        const shown = fit.chars > 0 && index === 0 ? shortenLabel(label.name, fit.chars) : clipLabel(label.name)
        const dot = labelDot(label.name)
        return (
          <ListTableHoverTarget
            key={labelKey(label, index)}
            card={shown === label.name ? null : <WrappedName dot={dot}>{label.name}</WrappedName>}
            className={cn(CHIP, "px-2 text-v2-list-faint")}
          >
            <Dot color={dot} />
            <ListTableHighlight text={shown} query={query} />
          </ListTableHoverTarget>
        )
      })}
      {rest.length > 0 ? <MoreChip rest={rest} /> : null}
    </span>
  )
}

/**
 * The due date's calendar, in three states on one shape: a ruled head while
 * the date is still to come; the head filled on the day itself, urgent without
 * being missed; and once it has passed, a cross set clear of the corner on the
 * card's own ground.
 */
const CalendarGlyph = ({ state, className }: { state: DueState; className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    className={cn("block size-[14px] shrink-0 fill-none stroke-current stroke-[1.7]", className)}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="3.6" y="4.6" width="16.8" height="16.8" rx="4.6" />
    {state === "upcoming" ? (
      <path d="M3.6 9.4h16.8" />
    ) : (
      <path d="M3.6 9.4V9.2a4.6 4.6 0 0 1 4.6-4.6h7.6a4.6 4.6 0 0 1 4.6 4.6v.2z" fill="currentColor" stroke="none" />
    )}
    {state === "late" ? (
      <>
        <circle cx="19.8" cy="19.8" r="5.9" stroke="none" style={{ fill: "hsl(var(--v2-card))" }} />
        <path d="M16.6 16.6l6.4 6.4M23 16.6l-6.4 6.4" strokeWidth="2" />
      </>
    ) : null}
  </svg>
)

/**
 * A due date as a pill. Muted for a date still to come; the mark alone goes red
 * on the day itself; the mark and the words go red once it has passed — unless
 * the work is done, when a passed date is no longer a warning. The three nearest
 * days are named rather than numbered.
 */
export const ListTableDueDate = ({ date, done = false, today, className }: {
  /** `YYYY-MM-DD`, or any date the browser can read. Nothing draws nothing. */
  date: string | Date | null | undefined
  done?: boolean
  /** The day the list is read on. Defaults to now; pass one to pin it in tests. */
  today?: Date
  className?: string
}) => {
  if (!date) return null
  const now = today ?? new Date()
  const state = dueState(date, done, now)
  return (
    <ListTableHoverTarget
      card={(
        <ListTableHoverRow
          icon={<CalendarGlyph state={state} className={state === "upcoming" ? undefined : "text-v2-list-late"} />}
        >
          {dueDistance(date, done, now)}
        </ListTableHoverRow>
      )}
      className={cn(
        CHIP,
        "box-border pl-[7px] pr-2",
        state === "late" ? "text-v2-list-late" : "text-v2-muted-foreground",
        className,
      )}
    >
      <CalendarGlyph state={state} className={state === "today" ? "text-v2-list-late" : undefined} />
      <span>{dueLabel(date, now)}</span>
    </ListTableHoverTarget>
  )
}

/**
 * A date as quiet text — `Aug 25`, or `May 2024` outside this year — with the
 * moment itself in the hover card.
 */
export const ListTableDate = ({ date, cardPrefix, today, className }: {
  date: string | Date | null | undefined
  /** Words before the moment in the hover card, for example "Created on". */
  cardPrefix?: string
  today?: Date
  className?: string
}) => {
  if (!date) return null
  const now = today ?? new Date()
  return (
    <ListTableHoverTarget
      card={<ListTableHoverRow>{[ cardPrefix, momentWords(date, now) ].filter(Boolean).join(" ")}</ListTableHoverRow>}
      className={cn("whitespace-nowrap text-[12px] leading-none text-v2-list-faint", className)}
    >
      {shortDate(date, now)}
    </ListTableHoverTarget>
  )
}

/** The pale ground and dark initial a name is drawn in, from the shared swatches. */
export const avatarColors = (name: string) => {
  const swatch = swatchFor(name)
  return { background: swatchColor(swatch, "soft"), color: swatchColor(swatch, "ink") }
}

export interface ListTableAvatarProps {
  name: string
  imageUrl?: string | null
  /** Diameter in pixels. */
  size?: number
  className?: string
}

/** Someone's picture, or their initial on the colour their name picks. */
export const ListTableAvatar = ({ name, imageUrl, size = 20, className }: ListTableAvatarProps) => {
  // 9px on the row's 20px circle, 10px on the hover card's 26px one.
  const style = { width: size, height: size, fontSize: Math.round(size * (size >= 26 ? 0.385 : 0.45)) }
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        className={cn("shrink-0 rounded-full object-cover", className)}
        style={style}
      />
    )
  }
  return (
    <span
      aria-hidden
      className={cn("inline-flex shrink-0 items-center justify-center rounded-full font-semibold leading-none", className)}
      style={{ ...style, ...avatarColors(name) }}
    >
      {name.trim().charAt(0).toUpperCase() || "?"}
    </span>
  )
}

/**
 * A person: their avatar, then their name, which fades out at the column's edge
 * rather than ending in an ellipsis. The hover card shows them whole, with a
 * second line — an address, a role — when there is one.
 */
export const ListTablePerson = ({ name, imageUrl, detail, className }: {
  name: string | null | undefined
  imageUrl?: string | null
  detail?: string | null
  className?: string
}) => {
  if (!name) return null
  return (
    <ListTableHoverTarget
      anchor="start"
      card={(
        <div className="flex items-center gap-2 px-[7px] py-1 text-[12px] leading-[18px] text-v2-list-ink">
          <ListTableAvatar name={name} imageUrl={imageUrl} size={26} />
          <span className="flex min-w-0 flex-col">
            <span className="truncate">{name}</span>
            {detail ? <span className="truncate text-v2-list-faint">{detail}</span> : null}
          </span>
        </div>
      )}
      className={cn("flex w-full min-w-0 items-center gap-2", className)}
    >
      <ListTableAvatar name={name} imageUrl={imageUrl} />
      <span
        className={cn(
          "min-w-0 flex-1 overflow-hidden whitespace-nowrap text-[13px] leading-[1.4] text-v2-muted-foreground",
          "[mask-image:linear-gradient(to_right,black_calc(100%-16px),transparent)]",
        )}
      >
        {name}
      </span>
    </ListTableHoverTarget>
  )
}
